/* usePauseAds.tsx */
import AdUtils from '@shared/utils/ads';
import AnalyticsInstance, { AnalyticEventType } from '@shared/utils/analytics';
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import Modals from '@shared/types/enums/modals';
import Pages from '@pages';
import Routes from '@shared/types/enums/routes';
import User from '@shared/classes/user';
import { AdSdkProvider } from '@shared/types/enums/ads';
import { Coordinates } from '@shared/utils/floatingMenu';
import { PlayerState } from '@shared/types/enums/player';
import { mediaSelector, play } from '@shared/store/slices/media.slice';
import { modalSelector } from '@shared/store/slices/modal.slice';
import { openFloatingMenu } from '@shared/store/thunks/floatingMenu.thunk';
import { setParams } from '@shared/store/slices/router.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useRef } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';
import {
  adsSelector,
  clearPauseAd,
  setImpressionsUrls,
  setPauseAd,
} from '@shared/store/slices/ads.slice';
import {
  filterFocus,
  popFocus,
  setFocus,
} from '@shared/store/slices/focus.slice';

/**
 * Props interface.
 *
 * @interface Props
 * @property {Event} [asset] - Optional event asset to display pause ad information for
 * @property {function} togglePilot - Function to toggle the pilot display
 * @property {function} keyHandler - Function to handle key events
 * @property {function} useKeyboard - Function to use the keyboard hook
 */
interface Props {
  asset?: Event;
  togglePilot: (display: boolean) => void;
  keyHandler: (key: string) => void;
  useKeyboard: (props: {
    focus?: Focus | number | Focus[] | number[];
    shouldFocus?: boolean;
    keyDownHandler?: (key: string) => void;
    keyPressHandler?: (() => void) | null;
    keyUpHandler?: (key: string) => void;
  }) => void;
}

/**
 * Custom hook that manages pause ad display logic.
 * Fetches and displays pause ads when the player is paused, and clears the modal when playback resumes.
 *
 * @param {Event} [asset] - Optional event asset to display pause ad information for
 */
export default function usePauseAds({
  asset,
  keyHandler,
  togglePilot,
  useKeyboard,
}: Props) {
  const { pauseAd } = useAppSelector(adsSelector);
  const { state } = useAppSelector(mediaSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const { planId } = useAppSelector(userSelector);
  const dispatch = useAppDispatch();
  const assetRef = useRef(asset);
  const planRef = useRef(User.getPlan(planId));
  const keyboardHandlerRef = useRef(keyHandler);
  assetRef.current = asset;
  keyboardHandlerRef.current = keyHandler;
  planRef.current = User.getPlan(planId);

  // ------------------------------
  // Callbacks
  // ------------------------------

  const loadPauseAd = useCallback(() => {
    const source = AdUtils.getSource(
      'pause',
      false,
      assetRef.current?.channel,
      true
    );
    if (source) {
      AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_REQUEST);
      AdUtils.fetchAdImage(
        source,
        {
          clicks: [],
          impressions: [],
        },
        ''
      ).then((response) => {
        if (response.url) {
          triggerRegularPauseAd(response);
        } else {
          const planSource = AdUtils.getSource(
            'pause',
            false,
            assetRef.current?.channel,
            true,
            false,
            true
          );
          AdUtils.fetchAdImage(
            planSource,
            {
              clicks: [],
              impressions: [],
            },
            ''
          ).then(triggerRegularPauseAd);
        }
      });
    }
  }, []);

  const triggerRegularPauseAd = useCallback(
    (response: {
      url?: string;
      selectionTrackers?: string[];
      impressionTrackers?: string[];
      adId?: string;
    }) => {
      // if(response.adId) {
      //   AnalyticsInstance.getOmnitrackManager()?.getPauseAdSession()?.setAdId(response.adId);
      // } TBD: This field is not approved for development
      AnalyticsInstance.getOmnitrackManager()
        ?.getPauseAdSession()
        ?.setPauseAdResponseAt(new Date().toISOString());
      if (!assetRef.current) return;
      if (response.url) {
        if (response.impressionTrackers) {
          response.impressionTrackers.forEach((item: string) => {
            AdUtils.sendImageImpression(item);
          });
          store.dispatch(setImpressionsUrls(response.impressionTrackers));
        }
        dispatch(
          setPauseAd({
            active: true,
            asset: assetRef.current,
            background: response.url,
            sdkProvider: AdSdkProvider.IMA,
          })
        );
        togglePilot(false);
      } else {
        AnalyticsInstance.getOmnitrackManager()
          ?.getPauseAdSession()
          ?.setWasAdShown(false);
        AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_RESPONSE);
      }
    },
    [assetRef.current?.id, assetRef.current?.channel?.id]
  );

  const triggerTeadsPauseAd = useCallback((placementId: string) => {
    if (!assetRef.current) return;
    if (!placementId) return;
    dispatch(
      setPauseAd({
        active: false,
        asset: assetRef.current,
        placementId,
        sdkProvider: AdSdkProvider.TEADS,
      })
    );
  }, []);

  // ------------------------------
  // Effect hooks
  // ------------------------------

  // Contruction & destruction logic
  useEffect(() => {
    return () => {
      const { router } = store.getState();
      if (
        !(
          router.route.stack.includes(Pages.PLAYER) ||
          router.route.stack.includes(Pages.ZAPPER)
        )
      ) {
        dispatch(clearPauseAd());
      }
    };
  }, []);

  // Focus management
  useEffect(() => {
    if (!pauseAd) {
      const { router, modal } = store.getState();
      if (!modal?.current) {
        if (router.route.Page === Pages.PLAYER) {
          dispatch(setFocus(Focus.PLAYER));
        } else if (router.route.Page === Pages.ZAPPER) {
          dispatch(setFocus(Focus.ZAPPER));
        }
      }
    }
    return () => {
      const { focus } = store.getState();
      if (focus.focus === Focus.PAUSE_ADS) {
        dispatch(popFocus());
      } else {
        dispatch(filterFocus(Focus.PAUSE_ADS));
      }
    };
  }, [pauseAd]);

  // Modal management
  useEffect(() => {
    if (pauseAd && modal === Modals.PLAYER_LANGUAGE) {
      dispatch(clearPauseAd());
      dispatch(play());
    }
  }, [pauseAd, modal]);

  // Player state change behaviour
  useEffect(() => {
    // On pause behaviour
    if (state === PlayerState.PAUSE) {
      // 1. Check if pause is enabled in plan: If not, return
      const planPause =
        planRef.current?.advertisement?.sections?.vastTags?.pause;
      if (!planPause?.enabled) return;

      // 2. Check if pause is enabled in channel: If not, return
      const channelPause =
        assetRef.current?.channel?.advertisement?.services?.vastTags?.pause;
      if (!channelPause?.enabled) return;

      // 3. Check the provider: If IMA, trigger IMA pause ad, if TEADS, trigger TEADS pause ad
      const channelProvider = channelPause?.sdkProvider;
      const planProvider = planPause?.sdkProvider;

      // 3.1 Check channel
      // 3.1.1 TEADS
      if (
        channelProvider?.name === AdSdkProvider.TEADS &&
        channelProvider?.placementId
      ) {
        triggerTeadsPauseAd(channelProvider?.placementId);
      }
      // 3.1.2 IMA
      else if (channelProvider?.name === AdSdkProvider.IMA) {
        loadPauseAd();
      }
      // 3.2 Check plan
      // 3.2.1 TEADS
      else if (
        planProvider?.name === AdSdkProvider.TEADS &&
        planProvider?.placementId
      ) {
        triggerTeadsPauseAd(planProvider?.placementId);
      }
      // 3.2.2 IMA
      else if (planProvider?.name === AdSdkProvider.IMA) {
        loadPauseAd();
      }
      // 4. Backwards compatibility
      else if (!channelProvider && !planProvider) {
        loadPauseAd();
      }
    }
  }, [state]);

  // ------------------------------
  // Keyboard hook
  // ------------------------------

  useKeyboard({
    focus: Focus.PAUSE_ADS,
    keyDownHandler: () => {
      return null;
    },
    keyUpHandler: (key) => {
      let shouldClear = false;
      let shouldPlay = false;
      let shouldTriggerKey = false;
      switch (key) {
        case 'SELECT':
        case 'BACK':
          shouldClear = true;
          shouldPlay = true;
          togglePilot(true);
          break;
        case 'CHANNEL_UP':
        case 'CHANNEL_DOWN':
        case 'REWIND':
        case 'LEFT':
        case 'FASTFORWARD':
        case 'RIGHT':
        case 'UP':
        case 'DOWN':
          const { router } = store.getState();
          if (router.route.Page === Pages.PLAYER) {
            if (key === 'CHANNEL_UP' || key === 'CHANNEL_DOWN') {
              shouldPlay = true;
            }
          }
          shouldClear = true;
          shouldTriggerKey = true;
          togglePilot(true);
          if (keyboardHandlerRef.current) {
            keyboardHandlerRef.current(key);
          }
          break;
      }
      setTimeout(() => {
        shouldPlay && dispatch(play());
        shouldTriggerKey && keyboardHandlerRef.current?.(key);
        shouldClear && dispatch(clearPauseAd());
      });
    },
    keyPressHandler: () => {
      const { ads, floatingMenu } = store.getState();
      if (!floatingMenu.open) {
        const { router, media } = store.getState();
        const params = router.params.current as {
          content: { keepWatchingMark: number };
        };
        if (params?.content)
          params.content.keepWatchingMark = media.currentTime;
        const stackParams = {
          ...params,
        };
        dispatch(setParams(stackParams));
        dispatch(
          openFloatingMenu({
            asset: assetRef.current,
            position: Coordinates.PAUSE_ADS,
            route:
              router.route.Page === Pages.ZAPPER
                ? Routes.ZAPPER
                : Routes.PLAYER,
          })
        );
      }
    },
  });
}
